/**
 * Credit Workflow Tab for NCR Edit
 *
 * Manages the credit process workflow including status stepper,
 * initiation form, and status progression actions.
 */

import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  DollarSign, FileText, CheckCircle2, CreditCard, XCircle, Upload, Download, Clock, Loader2,
} from 'lucide-react';
import type { NonConformanceReport as NCRType } from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';
import FileDropZone from '../../components/common/FileDropZone';

// =========================================================================
// Credit Workflow Status Stepper
// =========================================================================

const CREDIT_STEPS = [
  { key: 'requested', label: 'Requested', icon: DollarSign },
  { key: 'note_submitted', label: 'Credit Note', icon: FileText },
  { key: 'approved', label: 'Approved', icon: CheckCircle2 },
  { key: 'processed', label: 'Processed', icon: CreditCard },
] as const;

function CreditStatusStepper({ status }: { status: string }) {
  // Rejected branches off the supplier's credit-note step (index 1).
  const isRejected = status === 'rejected';
  const currentIdx = isRejected ? 1 : CREDIT_STEPS.findIndex(s => s.key === status);
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1 w-full">
        {CREDIT_STEPS.map((step, idx) => {
          const Icon = step.icon;
          const isCompleted = !isRejected && idx < currentIdx;
          const isCurrent = !isRejected && idx === currentIdx;
          return (
            <div key={step.key} className="flex items-center flex-1">
              <div className="flex flex-col items-center flex-1">
                <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-medium ${
                  isCompleted ? 'bg-green-100 text-green-700' :
                  isCurrent ? 'bg-blue-100 text-blue-700 ring-2 ring-blue-300' :
                  'bg-gray-100 text-gray-400'
                }`}>
                  <Icon className="h-4 w-4" />
                </div>
                <span className={`text-[10px] mt-1 ${
                  isCompleted ? 'text-green-700 font-medium' :
                  isCurrent ? 'text-blue-700 font-medium' :
                  'text-gray-400'
                }`}>{step.label}</span>
              </div>
              {idx < CREDIT_STEPS.length - 1 && (
                <div className={`h-0.5 flex-1 mx-1 ${
                  !isRejected && idx < currentIdx ? 'bg-green-300' : 'bg-gray-200'
                }`} />
              )}
            </div>
          );
        })}
      </div>
      {isRejected && (
        <div className="flex items-center gap-2 p-2 bg-red-50 rounded border border-red-200 text-sm text-red-700">
          <XCircle className="h-4 w-4" />
          Credit has been rejected
        </div>
      )}
    </div>
  );
}

// =========================================================================
// Supplier: submit / re-submit the credit note (document + number required)
// =========================================================================

function SubmitCreditNoteForm({ saving, heading, onSubmit }: {
  saving: boolean;
  heading: string;
  onSubmit: (data: FormData) => void;
}) {
  const [form, setForm] = useState({ credit_note_reference: '', credit_issue_date: '', supplier_notes: '' });
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = () => {
    if (!form.credit_note_reference.trim()) { setError('Enter the credit note number.'); return; }
    if (!file) { setError('Attach the credit note document.'); return; }
    setError(null);
    const fd = new FormData();
    fd.append('credit_note_reference', form.credit_note_reference.trim());
    if (form.credit_issue_date) fd.append('credit_issue_date', form.credit_issue_date);
    if (form.supplier_notes) fd.append('supplier_notes', form.supplier_notes);
    fd.append('credit_note_document', file);
    onSubmit(fd);
  };

  return (
    <div className="border-t pt-4 space-y-4">
      <h4 className="font-medium text-sm">{heading}</h4>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Credit Note Number <span className="text-red-500">*</span></Label>
          <Input
            value={form.credit_note_reference}
            onChange={e => setForm(f => ({ ...f, credit_note_reference: e.target.value }))}
            placeholder="Credit note number"
          />
        </div>
        <div className="space-y-2">
          <Label>Issue Date</Label>
          <Input
            type="date"
            value={form.credit_issue_date}
            onChange={e => setForm(f => ({ ...f, credit_issue_date: e.target.value }))}
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Credit Note Document <span className="text-red-500">*</span></Label>
        {/* Customer review 11 Sep (drag and drop everywhere). */}
        <FileDropZone
          size="compact"
          accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx"
          hint={file ? <>Selected: <span className="font-medium text-foreground">{file.name}</span></> : 'PDF, image, Word or Excel'}
          onFiles={(files) => setFile(files[0])}
          data-testid="ncr-credit-note-drop-zone"
        />
      </div>
      <div className="space-y-2">
        <Label>Notes</Label>
        <Textarea
          value={form.supplier_notes}
          onChange={e => setForm(f => ({ ...f, supplier_notes: e.target.value }))}
          rows={2}
          placeholder="Optional notes..."
        />
      </div>
      {error && (
        <p className="text-sm text-red-600 flex items-center gap-1"><XCircle className="h-4 w-4" />{error}</p>
      )}
      <Button onClick={handleSubmit} disabled={saving}>
        {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
        Submit Credit Note
      </Button>
    </div>
  );
}

// =========================================================================
// Credit Workflow Tab
// =========================================================================

export interface CreditWorkflowTabProps {
  ncr: NCRType;
  isCustomer: boolean;
  isSupplier: boolean;
  saving: boolean;
  onInitiate: (data: { credit_amount?: number; credit_currency?: string; credit_reason?: string; customer_notes?: string }) => void;
  onSubmitNote: (data: FormData) => void;
  onUpdateStatus: (status: string, data?: Record<string, unknown>) => void;
}

export function CreditWorkflowTab({ ncr, isCustomer, isSupplier, saving, onInitiate, onSubmitNote, onUpdateStatus }: CreditWorkflowTabProps) {
  const { formatDate } = useFormatters();
  const [initForm, setInitForm] = useState({ credit_amount: '', credit_reason: '', customer_notes: '' });
  const [approvalForm, setApprovalForm] = useState({ credit_amount_confirmed: '', customer_credit_reference: '', approval_notes: '' });

  const process = ncr.credit_process;

  if (!ncr.is_credit_required) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center py-8 text-muted-foreground">
            Credit is not required for this NCR.
          </div>
        </CardContent>
      </Card>
    );
  }

  // If no process exists yet, show initiation form
  if (!process) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Initiate Credit Process
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            A credit note is required for this NCR. Start the credit process below.
          </p>
          {ncr.is_collection_required && !ncr.collection_completed && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-700">
              Collection must be completed before the credit process can proceed.
            </div>
          )}
          {isCustomer ? (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Credit Amount</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={initForm.credit_amount}
                    onChange={e => setInitForm(f => ({ ...f, credit_amount: e.target.value }))}
                    placeholder="0.00"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Credit Reason</Label>
                  <Input
                    value={initForm.credit_reason}
                    onChange={e => setInitForm(f => ({ ...f, credit_reason: e.target.value }))}
                    placeholder="Reason for credit request"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={initForm.customer_notes}
                  onChange={e => setInitForm(f => ({ ...f, customer_notes: e.target.value }))}
                  placeholder="Additional details..."
                  rows={3}
                />
              </div>
              <Button
                onClick={() => onInitiate({
                  credit_amount: initForm.credit_amount ? parseFloat(initForm.credit_amount) : undefined,
                  credit_reason: initForm.credit_reason,
                  customer_notes: initForm.customer_notes,
                })}
                disabled={saving}
              >
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CreditCard className="h-4 w-4 mr-2" />}
                Initiate Credit
              </Button>
            </>
          ) : (
            <p className="text-sm text-amber-600">Waiting for the customer to initiate the credit process.</p>
          )}
        </CardContent>
      </Card>
    );
  }

  // Process exists - show status stepper and actions
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="h-5 w-5" />
          Credit Process
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <CreditStatusStepper status={process.status} />

        {/* Process details */}
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground">Requested by:</span>{' '}
            <span className="font-medium">{process.requested_by_name}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Requested:</span>{' '}
            <span className="font-medium">{formatDate(process.requested_date)}</span>
          </div>
          {process.credit_amount && (
            <div>
              <span className="text-muted-foreground">Amount:</span>{' '}
              <span className="font-medium">{process.credit_currency} {parseFloat(process.credit_amount).toFixed(2)}</span>
            </div>
          )}
          {process.credit_amount_confirmed && (
            <div>
              <span className="text-muted-foreground">Confirmed amount:</span>{' '}
              <span className="font-medium">{process.credit_currency} {parseFloat(process.credit_amount_confirmed).toFixed(2)}</span>
            </div>
          )}
          {process.credit_reason && (
            <div className="col-span-2">
              <span className="text-muted-foreground">Reason:</span>{' '}
              <span className="font-medium">{process.credit_reason}</span>
            </div>
          )}
          {process.credit_note_reference && (
            <div>
              <span className="text-muted-foreground">Credit note no:</span>{' '}
              <span className="font-medium">{process.credit_note_reference}</span>
            </div>
          )}
          {process.credit_note_document && (
            <div>
              <span className="text-muted-foreground">Credit note:</span>{' '}
              <a
                href={process.credit_note_document}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-primary inline-flex items-center gap-1 hover:underline"
              >
                <Download className="h-3.5 w-3.5" /> View document
              </a>
            </div>
          )}
          {process.credit_debit_number && (
            <div>
              <span className="text-muted-foreground">Credit/debit no:</span>{' '}
              <span className="font-medium">{process.credit_debit_number}</span>
            </div>
          )}
          {process.customer_credit_reference && (
            <div>
              <span className="text-muted-foreground">Customer ref:</span>{' '}
              <span className="font-medium">{process.customer_credit_reference}</span>
            </div>
          )}
        </div>

        {/* Notes display */}
        {(process.customer_notes || process.supplier_notes || process.approval_notes) && (
          <div className="space-y-2 border-t pt-4">
            {process.customer_notes && (
              <div className="text-sm"><span className="text-muted-foreground">Customer notes:</span> {process.customer_notes}</div>
            )}
            {process.supplier_notes && (
              <div className="text-sm"><span className="text-muted-foreground">Supplier notes:</span> {process.supplier_notes}</div>
            )}
            {process.approval_notes && (
              <div className="text-sm"><span className="text-muted-foreground">Approval notes:</span> {process.approval_notes}</div>
            )}
          </div>
        )}

        {/* Actions based on current status */}

        {/* Supplier: submit the credit note (requested -> note_submitted) */}
        {process.status === 'requested' && isSupplier && (
          <SubmitCreditNoteForm saving={saving} heading="Submit Credit Note" onSubmit={onSubmitNote} />
        )}

        {/* Customer: waiting for the supplier's credit note */}
        {process.status === 'requested' && isCustomer && (
          <div className="border-t pt-4 flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="h-4 w-4" /> Awaiting the supplier to upload the credit note.
          </div>
        )}

        {/* Customer: approve or reject the submitted credit note */}
        {process.status === 'note_submitted' && isCustomer && (
          <div className="border-t pt-4 space-y-4">
            <h4 className="font-medium text-sm">Review Credit Note</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Confirmed Amount</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={approvalForm.credit_amount_confirmed}
                  onChange={e => setApprovalForm(f => ({ ...f, credit_amount_confirmed: e.target.value }))}
                  placeholder={process.credit_amount || '0.00'}
                />
              </div>
              <div className="space-y-2">
                <Label>Customer Reference</Label>
                <Input
                  value={approvalForm.customer_credit_reference}
                  onChange={e => setApprovalForm(f => ({ ...f, customer_credit_reference: e.target.value }))}
                  placeholder="Your internal reference"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={approvalForm.approval_notes}
                onChange={e => setApprovalForm(f => ({ ...f, approval_notes: e.target.value }))}
                placeholder="Approval or rejection notes..."
                rows={2}
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={() => onUpdateStatus('approved', {
                credit_amount_confirmed: approvalForm.credit_amount_confirmed ? parseFloat(approvalForm.credit_amount_confirmed) : undefined,
                customer_credit_reference: approvalForm.customer_credit_reference,
                approval_notes: approvalForm.approval_notes,
              })} disabled={saving} className="bg-green-600 hover:bg-green-700">
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Approve &amp; Close
              </Button>
              <Button variant="destructive" onClick={() => onUpdateStatus('rejected', {
                approval_notes: approvalForm.approval_notes,
              })} disabled={saving}>
                <XCircle className="h-4 w-4 mr-2" />
                Reject
              </Button>
            </div>
          </div>
        )}

        {/* Supplier: waiting for customer approval */}
        {process.status === 'note_submitted' && isSupplier && (
          <div className="border-t pt-4 flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="h-4 w-4" /> Credit note submitted. Awaiting customer approval.
          </div>
        )}

        {/* Supplier: amend and re-submit after a rejection */}
        {process.status === 'rejected' && isSupplier && (
          <SubmitCreditNoteForm saving={saving} heading="Amend &amp; Re-submit Credit Note" onSubmit={onSubmitNote} />
        )}

        {/* Customer: waiting for the supplier to resubmit */}
        {process.status === 'rejected' && isCustomer && (
          <div className="border-t pt-4 flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="h-4 w-4" /> Rejected. Awaiting the supplier to amend and resubmit.
          </div>
        )}

        {process.status === 'processed' && (
          <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg border border-green-200">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            <div>
              <h3 className="font-semibold text-green-800">Credit Processed</h3>
              {process.processed_date && (
                <p className="text-sm text-green-600">
                  Processed on {formatDate(process.processed_date)}
                  {process.processed_by_name && ` by ${process.processed_by_name}`}
                </p>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
